import { DateRangeType, DefaultButton, PrimaryButton, Spinner, SpinnerSize } from "@fluentui/react";
import { add, endOfWeek, format, isEqual, startOfWeek } from "date-fns";
import { ArrowLeft16Regular, ArrowRight16Regular } from "@fluentui/react-icons";
import { Calendar } from "../../calendar/Calendar";
import { DraftControl } from "../../DraftControl/DraftControl";
import React, { useContext, useEffect, useState } from "react";
import { TView } from "../Scheduler";
import { shiftsContext } from "../../../providers/shifts";
import './assets/header.scss'

interface IProps {
    increaseDate: () => void
    decreaseDate: () => void
    setToCurrentWeek: () => void
    date: Date
    updateDateRange: (date: Date[]) => void
    dateRange: Date[]
    updateView: (view: TView) => void
    view: TView
    setDate: (date: Date) => void
    showOtherSiteShifts: boolean
    toggleShowOtherSitesShifts: () => void
    loadingPayrollData: boolean,
    openNewJobModal: () => void;
    inFlightCount: number;
    payrollRange?: { start: Date; end: Date } | null;
}

const Header = (props: IProps) => {

    const { siteLocation } = useContext(shiftsContext);

    const [isFullscreen, setIsFullscreen] = useState(false);

    useEffect(() => {
        const fullscreenToggle = () => {
            try {
                setIsFullscreen(window.self === window.top);
            } catch (e) {
                setIsFullscreen(false);
            }
        }
        fullscreenToggle()

        window.addEventListener('fullscreenchange mozfullscreenchange webkitfullscreenchange msfullscreenchange', fullscreenToggle)

        return () => window.removeEventListener('fullscreenchange mozfullscreenchange webkitfullscreenchange msfullscreenchange', fullscreenToggle)
    }, [])

    const renderDate = () => {
        const { date, view, payrollRange, dateRange } = props
        if (!date) {
            return ('')
        }

        // Build a display range: "showing" defaults to view length, "payroll" uses full cycle if available.
        const fallbackEnd = new Date(date.getTime());
        fallbackEnd.setDate(fallbackEnd.getDate() + (view === "resourceTimelineFortnight" ? 13 : 6))
        const showingStart = dateRange?.[0] ?? date;
        const showingEnd = dateRange?.[1] ?? fallbackEnd;
        const payrollStart = payrollRange?.start ?? showingStart;
        const payrollEnd = payrollRange?.end ?? showingEnd;

        // Long form range for the payroll cycle line in the header.
        const formatRange = (start: Date, end: Date) => {
            const startYear = format(start, 'yyyy');
            const endYear = format(end, 'yyyy');
            if (startYear !== endYear) {
                return `${format(start, 'do MMMM, yyyy')} - ${format(end, 'do MMMM, yyyy')}`;
            }
            return `${format(start, 'do MMMM')} - ${format(end, 'do MMMM')}, ${startYear}`;
        }

        // Short form range for the "Showing" line in the header.
        const formatRangeShort = (start: Date, end: Date) => {
            const startYear = format(start, 'yyyy');
            const endYear = format(end, 'yyyy');
            if (startYear !== endYear) {
                return `${format(start, 'do MMM, yyyy')} - ${format(end, 'do MMM, yyyy')}`;
            }
            return `${format(start, 'do MMM')} - ${format(end, 'do MMM')}`;
        }

        // Two-line header: full payroll cycle + current visible slice.
        return (
            <span>
                <span style={{ display: 'block', fontSize: 18, color: '#4a4341' }}>
                    Payroll cycle: {formatRange(payrollStart, payrollEnd)}
                </span>
                <span style={{ display: 'block', fontSize: 15, color: '#cf541e' }}>
                    Showing: {formatRangeShort(showingStart, showingEnd)}
                </span>
            </span>
        )
    }

    const openFullScreen = () => {
        window.open(window.location.href, '_blank')
    }
    const isInIframe = () => {
        try {
            return window.self !== window.top;
        } catch (e) {
            return true;
        }
    }

    // Build a safe fallback in case dateRange is briefly unavailable during view/date transitions.
    // If this happens, compute what range we are already looking at based on the visible board slice and pass this to the small calendar.
    const fallbackViewEnd = add(props.date, { days: props.view === "resourceTimelineWeek" ? 6 : 13 });
    const fallbackViewRange: Date[] = [props.date, fallbackViewEnd];

    // Keep selector shading aligned with the active view:
    // week view highlights only the visible slice; fortnight highlights full payroll cycle.
    const selectorDateRange =
        props.view === "resourceTimelineWeek"
            ? (props.dateRange?.length ? props.dateRange : fallbackViewRange)
            : (props.payrollRange ? [props.payrollRange.start, props.payrollRange.end] : (props.dateRange?.length ? props.dateRange : fallbackViewRange));

    return (
        <div id={"calHeader"} className="rb-header" style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 10px 10px 10px', borderBottom: '1px solid #f2f2f2', boxShadow: '0 0 3px rgba(0,0,0,0.1)' }}>
            <div style={{ display: 'inline-flex', gap: '10px', alignItems: 'center' }}>
                {!isFullscreen
                    ? <PrimaryButton onClick={openFullScreen}> Full Screen </PrimaryButton>
                    : <p style={{ margin: '0 5px 0 0' }}><strong>Site:</strong> {siteLocation}</p>
                }
                <DefaultButton
                    onClick={() => props.updateView(props.view === "resourceTimelineWeek" ? 'resourceTimelineFortnight' : 'resourceTimelineWeek')}
                >
                    {props.view === "resourceTimelineWeek" ? 'Change to Fortnight View' : 'Change to Week View'}
                </DefaultButton>
                <DefaultButton onClick={props.toggleShowOtherSitesShifts}>
                    {props.showOtherSiteShifts ? 'Change to Current Site' : 'Change to All Sites'}
                </DefaultButton>
                {isInIframe() && <DefaultButton onClick={props.openNewJobModal}>
                    New Job
                </DefaultButton>}
            </div>
            <div style={{ display: 'inline-flex', alignItems: 'center' }}>
                <DefaultButton disabled={props.loadingPayrollData} onClick={props.decreaseDate} style={{
                    width: "40px",
                    padding: "0",
                    minWidth: 'unset'
                }}><ArrowLeft16Regular /></DefaultButton>

                <Calendar date={props.date} setDateRange={props.updateDateRange}
                    dateRange={selectorDateRange} // Week: showing range. Fortnight: payroll cycle range.
                    setDate={props.setDate} dateRangeType={DateRangeType.Day}
                    setToCurrentWeek={props.setToCurrentWeek}
                    setToCurrentWeekButtonDisabled={isEqual(props.date, startOfWeek(new Date(), { weekStartsOn: 1 }))}
                    dateRender={renderDate()}
                />

                <DefaultButton disabled={props.loadingPayrollData} onClick={props.increaseDate} style={{
                    width: "40px",
                    padding: "0",
                    minWidth: 'unset'
                }}><ArrowRight16Regular /></DefaultButton>
            </div>
            {/* Keep the draft controls visible while showing background activity */}
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                {props.inFlightCount > 0 && (
                    // Small indicator that some requests are still in flight.
                    <div className="rb-inflight-indicator" title={`${props.inFlightCount} operation(s) in flight`}>
                        <Spinner size={SpinnerSize.small} />
                        <span className="rb-inflight-label">Pending</span>
                        <span>{props.inFlightCount}</span>
                    </div>
                )}
                <DraftControl />
            </div>

        </div>
    )
}


export { Header }
